import {
  EDurationUnit,
  ETriggerTiming,
  ETriggerUnit,
  type TChatbotGeneralFormValues,
} from './chatbot-form.type'
import type { TChatbotResponse } from './chatbot-api.type'
import {
  parseIsoDuration as parseGenericIsoDuration,
  parseCombinedIsoDuration,
  EIsoTimeUnit,
} from 'src/utils/duration-iso.util'

function mapToTriggerUnit(isoUnit: EIsoTimeUnit): ETriggerUnit {
  switch (isoUnit) {
    case EIsoTimeUnit.HOURS:
      return ETriggerUnit.HOURS
    case EIsoTimeUnit.DAYS:
      return ETriggerUnit.DAYS
    case EIsoTimeUnit.WEEKS:
      return ETriggerUnit.WEEKS
    case EIsoTimeUnit.MONTHS:
      return ETriggerUnit.MONTHS
    case EIsoTimeUnit.MINUTES:
      throw new Error('MINUTES is not supported for trigger units')
    default:
      throw new Error(`Unsupported ISO unit for trigger: ${isoUnit}`)
  }
}

export function parseIsoDurationForTrigger(isoDuration: string): {
  value: number
  unit: ETriggerUnit
  timing: ETriggerTiming
} {
  const parsed = parseGenericIsoDuration(isoDuration)

  return {
    value: parsed.value,
    unit: mapToTriggerUnit(parsed.unit),
    timing: parsed.sign === '-' ? ETriggerTiming.BEFORE : ETriggerTiming.AFTER,
  }
}

export function parseIsoDurationForOptions(isoDuration: string): {
  value: number
  unit: EDurationUnit
} {
  const { days, hours, minutes } = parseCombinedIsoDuration(isoDuration)

  const isSingleUnit = [days > 0, hours > 0, minutes > 0].filter(Boolean).length <= 1
  if (!isSingleUnit) {
    return { value: days * 24 * 60 + hours * 60 + minutes, unit: EDurationUnit.MINUTES }
  }
  if (days > 0) {
    return { value: days, unit: EDurationUnit.DAYS }
  }
  if (hours > 0) {
    return { value: hours, unit: EDurationUnit.HOURS }
  }
  return { value: minutes, unit: EDurationUnit.MINUTES }
}

export function parseApiToFormValues(apiData: TChatbotResponse): TChatbotGeneralFormValues {
  // Parse schedule duration
  const {
    value: triggerDelay,
    unit: triggerUnit,
    timing: triggerTiming,
  } = parseIsoDurationForTrigger(apiData.schedule.durationDiff)

  // Parse automaticEnd option if present
  let automaticEnd: TChatbotGeneralFormValues['options']['automaticEnd'] = null
  if (apiData.options.automaticEnd) {
    const { value: expireDuration, unit: expireDurationUnit } = parseIsoDurationForOptions(
      apiData.options.automaticEnd.expireDuration,
    )

    automaticEnd = {
      expireDuration,
      expireDurationUnit,
      expirationMessage: apiData.options.automaticEnd.expirationMessage,
    }
  }

  const incompleteQuestionnaireDelay =
    apiData.options.humanReviewConfig?.incompleteQuestionnaireDelay ??
    apiData.options.showUnreachedPatientInDashboardDelay ??
    null

  const requestHumanReview = apiData.options.humanReviewConfig
    ? { ...apiData.options.humanReviewConfig, incompleteQuestionnaireDelay }
    : incompleteQuestionnaireDelay
      ? { incompleteQuestionnaireDelay }
      : null

  return {
    name: apiData.name,
    description: apiData.description,
    schedule: {
      triggerDelay,
      triggerUnit,
      triggerTiming,
      sendAt: apiData.schedule.sendAt,
      allowedDays: [1, 2, 3, 4, 5, 6, 7].filter((day) => !apiData.schedule.avoidedWeekdays.includes(day)),
      medeventListIds: apiData.schedule.medeventListIds || apiData.medEventIds,
      avoidHolidays: apiData.schedule.avoidHolidays,
    },
    options: {
      automaticEnd,
      requestHumanReview,
      automaticRelaunchDelay: apiData.options.automaticRelaunchDelay ?? null,
    },
  }
}
